import type { ReactNode } from 'react';

interface SearchResultsWithMapProps {
  /** The results column: cards, grid, empty state, pagination — whatever the page lists. */
  children: ReactNode;
  /** Map panel. Rendered inside the standard framed box; omit to render results full width. */
  map?: ReactNode;
  /** Optional node directly under the map frame, e.g. an expand/collapse button. */
  mapFooter?: ReactNode;
  /**
   * Render the map full width above the results instead of in a sticky right-hand
   * sidebar. Used by the Wineries search page's expanded-map mode.
   */
  stacked?: boolean;
}

/**
 * Shared two-column shell for search-style pages: a results column beside a
 * sticky map panel on desktop, results only on narrow screens.
 *
 * Owns the column widths, sticky offset, map frame, and map height so the
 * Wineries, Experiences, and Events pages stay in step as the responsive
 * behavior evolves.
 */
export const SearchResultsWithMap = ({
  children,
  map,
  mapFooter,
  stacked = false,
}: SearchResultsWithMapProps) => {
  const mapColumn = map ? (
    <div
      className={
        stacked ? 'hidden lg:block w-full' : 'hidden lg:block w-[40%] flex-shrink-0 sticky top-4'
      }
    >
      <div
        className={`w-full rounded-lg overflow-hidden border border-gray-200 shadow-sm ${
          stacked ? 'h-[60vh]' : 'h-[calc(50vh-3rem)]'
        }`}
      >
        {map}
      </div>
      {mapFooter}
    </div>
  ) : null;

  return (
    <div className={stacked ? 'flex flex-col gap-4' : 'flex gap-4'}>
      {stacked && mapColumn}
      <div className="flex-1 min-w-0">{children}</div>
      {!stacked && mapColumn}
    </div>
  );
};
